/**
 * 🎯 AdminThemes — Dashboard d'administration des thèmes P1.0
 * 
 * CRUD complet pour les thèmes coach :
 * - Ajouter / Modifier / Supprimer
 * - Activer / Désactiver
 * - Réorganiser l'ordre
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Edit2, Trash2, Check, X, Lightbulb, 
  Briefcase, Heart, GripVertical, ToggleLeft, ToggleRight,
  Save, ArrowLeft, AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminThemes = () => {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTheme, setEditingTheme] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Formulaire
  const [formData, setFormData] = useState({
    slug: '',
    label: '',
    category: 'professional',
    description: '',
    prompt_context: '',
    order: 0,
    is_active: true
  });

  const token = localStorage.getItem('token');

  // Charger les thèmes
  const fetchThemes = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/themes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur chargement thèmes');
      const data = await response.json();
      setThemes(data);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  // Grouper par catégorie
  const professionalThemes = themes.filter(t => t.category === 'professional');
  const personalThemes = themes.filter(t => t.category === 'personal');

  // Handlers
  const handleCreate = () => {
    setIsCreating(true);
    setEditingTheme(null);
    setFormData({
      slug: '',
      label: '',
      category: 'professional',
      description: '',
      prompt_context: '',
      order: themes.length + 1,
      is_active: true
    });
  };

  const handleEdit = (theme) => {
    setEditingTheme(theme.id);
    setIsCreating(false);
    setFormData({
      slug: theme.slug,
      label: theme.label,
      category: theme.category,
      description: theme.description || '',
      prompt_context: theme.prompt_context || '',
      order: theme.order,
      is_active: theme.is_active
    });
  };

  const handleCancel = () => {
    setEditingTheme(null);
    setIsCreating(false);
    setFormData({
      slug: '',
      label: '',
      category: 'professional',
      description: '',
      prompt_context: '',
      order: 0,
      is_active: true
    });
  };

  const handleSave = async () => {
    try {
      setSaveStatus('saving');
      
      const url = isCreating 
        ? `${API_URL}/api/admin/themes`
        : `${API_URL}/api/admin/themes/${editingTheme}`;
      
      const method = isCreating ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur sauvegarde');
      }

      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 2000);
      
      handleCancel();
      fetchThemes();
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setSaveStatus('error');
      setError(err.message);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleDelete = async (themeId) => {
    if (!window.confirm('Supprimer ce thème ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/themes/${themeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur suppression');
      
      fetchThemes();
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError(err.message);
    }
  };

  const handleToggleActive = async (theme) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/themes/${theme.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !theme.is_active })
      });
      
      if (!response.ok) throw new Error('Erreur mise à jour');
      
      fetchThemes();
    } catch (err) {
      console.error('Erreur toggle:', err);
      setError(err.message);
    }
  };

  const handleSeedThemes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/themes/seed`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Erreur seed');
      
      const result = await response.json();
      alert(`${result.created} thèmes créés, ${result.skipped} ignorés`);
      fetchThemes();
    } catch (err) {
      console.error('Erreur seed:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/admin" 
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Lightbulb className="text-amber-500" />
                  Gestion des Thèmes
                </h1>
                <p className="text-sm text-gray-500">Coach Libre P1.0</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSeedThemes}
                className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Initialiser par défaut
              </button>
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus size={18} />
                Nouveau thème
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-red-500" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={18} className="text-red-500" />
            </button>
          </div>
        )}

        {/* Formulaire création/édition */}
        {(isCreating || editingTheme) && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {isCreating ? 'Nouveau thème' : 'Modifier le thème'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slug (identifiant unique)
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/\s/g, '-')})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="leadership"
                  disabled={!isCreating}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label (affiché)
                </label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({...formData, label: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Leadership"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="professional">Professionnel</option>
                  <option value="personal">Personnel</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordre d'affichage
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="0"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (tooltip)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Posture, autorité, influence"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contexte prompt (injecté dans Owen)
                </label>
                <textarea
                  value={formData.prompt_context}
                  onChange={(e) => setFormData({...formData, prompt_context: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                  placeholder="leadership et posture managériale"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">
                  Thème actif (visible utilisateur)
                </label>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.slug || !formData.label}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
                  ${!formData.slug || !formData.label
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : saveStatus === 'saving'
                      ? 'bg-purple-400 text-white'
                      : saveStatus === 'success'
                        ? 'bg-green-600 text-white'
                        : saveStatus === 'error'
                          ? 'bg-red-600 text-white'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                  }
                `}
              >
                {saveStatus === 'saving' ? (
                  <>Enregistrement...</>
                ) : saveStatus === 'success' ? (
                  <><Check size={18} /> Enregistré</>
                ) : saveStatus === 'error' ? (
                  <><X size={18} /> Erreur</>
                ) : (
                  <><Save size={18} /> Enregistrer</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Liste des thèmes par catégorie */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Thèmes professionnels */}
          <ThemeCategory
            title="Professionnel"
            icon={<Briefcase className="text-blue-500" />}
            themes={professionalThemes}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            editingId={editingTheme}
          />
          
          {/* Thèmes personnels */}
          <ThemeCategory
            title="Personnel"
            icon={<Heart className="text-rose-500" />}
            themes={personalThemes}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            editingId={editingTheme}
          />
        </div>

        {/* Stats */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Statistiques</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Total" value={themes.length} />
            <Stat label="Actifs" value={themes.filter(t => t.is_active).length} color="green" />
            <Stat label="Professionnels" value={professionalThemes.length} color="blue" />
            <Stat label="Personnels" value={personalThemes.length} color="rose" />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Catégorie de thèmes
 */
const ThemeCategory = ({ title, icon, themes, onEdit, onDelete, onToggleActive, editingId }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
        {icon}
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <span className="ml-auto text-sm text-gray-500">{themes.length} thèmes</span>
      </div>
      
      {themes.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          Aucun thème dans cette catégorie
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {themes.map(theme => (
            <ThemeRow
              key={theme.id}
              theme={theme}
              onEdit={() => onEdit(theme)}
              onDelete={() => onDelete(theme.id)}
              onToggleActive={() => onToggleActive(theme)}
              isEditing={editingId === theme.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Ligne de thème
 */
const ThemeRow = ({ theme, onEdit, onDelete, onToggleActive, isEditing }) => {
  return (
    <div className={`
      flex items-center gap-3 px-4 py-3 transition-colors
      ${isEditing ? 'bg-purple-50' : 'hover:bg-gray-50'}
      ${!theme.is_active ? 'opacity-50' : ''}
    `}>
      <GripVertical size={16} className="text-gray-400 cursor-grab" />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{theme.label}</span>
          <span className="text-xs text-gray-400">({theme.slug})</span>
          {!theme.is_active && (
            <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
              Inactif
            </span>
          )}
        </div>
        {theme.description && (
          <p className="text-sm text-gray-500 truncate">{theme.description}</p>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={onToggleActive}
          className={`p-1.5 rounded-lg transition-colors ${
            theme.is_active 
              ? 'text-green-600 hover:bg-green-50' 
              : 'text-gray-400 hover:bg-gray-100'
          }`}
          title={theme.is_active ? 'Désactiver' : 'Activer'}
        >
          {theme.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
        </button>
        
        <button
          onClick={onEdit}
          className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
          title="Modifier"
        >
          <Edit2 size={16} />
        </button>
        
        <button
          onClick={onDelete}
          className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          title="Supprimer"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

/**
 * Statistique simple
 */
const Stat = ({ label, value, color = 'purple' }) => {
  const colors = {
    purple: 'text-purple-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    rose: 'text-rose-600'
  };
  
  return (
    <div className="text-center">
      <p className={`text-2xl font-bold ${colors[color]}`}>{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
};

export default AdminThemes;
